/** Google Ads Conversion-Tracking (gtag.js wird in public/index.html geladen) + granulare Cookie-Einwilligung. */

export const CONSENT_KEY = 'fuelx_cookie_consent_v2';
export const CONSENT_KEY_V3 = 'fuelx_cookie_consent_v3';
export const COOKIE_CONSENT_VERSION = '3.0';

/** Liest die granulare Einwilligung (v3, Fallback auf Legacy-Format v2). */
export function getConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY_V3);
    if (raw) {
      const c = JSON.parse(raw);
      return { analytics: !!c.analytics, marketing: !!c.marketing, choice: c.choice || 'custom' };
    }
    const legacy = localStorage.getItem(CONSENT_KEY);
    if (legacy === 'all') return { analytics: true, marketing: true, choice: 'all' };
    if (legacy === 'essential') return { analytics: false, marketing: false, choice: 'essential' };
  } catch { /* noop */ }
  return null;
}

export const hasMarketingConsent = () => Boolean(getConsent()?.marketing);

/** Wendet die Kategorien an: Statistik → GA4, Marketing → Google Ads + Meta Pixel (Consent Mode v2). */
export function applyConsent({ analytics, marketing }) {
  if (typeof window === 'undefined') return;
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      ad_storage: marketing ? 'granted' : 'denied',
      ad_user_data: marketing ? 'granted' : 'denied',
      ad_personalization: marketing ? 'granted' : 'denied',
      analytics_storage: analytics ? 'granted' : 'denied',
    });
  }
  if (typeof window.fbq === 'function') {
    window.fbq('consent', marketing ? 'grant' : 'revoke');
  }
  // Bei Marketing-Consent: IDs sichern, Pixel-Advanced-Matching setzen und das CAPI-Gegenstück
  // zum initialen PageView nachziehen (verbessert CAPI-Coverage & Match-Qualität).
  if (marketing) {
    ensureMetaIds();
    setPixelAdvancedMatching();
    sendInitialPageViewCapi();
    // OpenAI-Pixel erst nach Marketing-Consent laden + Session-Start-PageView nachziehen.
    try { if (typeof window.__fxLoadOaiq === 'function') window.__fxLoadOaiq(); } catch { /* noop */ }
    trackOaiq('page_viewed', { once: 'oai_pv_session' });
  }
}

const visitorId = () => {
  try {
    let id = localStorage.getItem('fuelx_visitor_id');
    if (!id) {
      id = window.crypto?.randomUUID ? window.crypto.randomUUID() : `v_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem('fuelx_visitor_id', id);
    }
    // external_id zusätzlich als Cookie spiegeln → Backend liest es bei Bestellanlage
    // und reichert das serverseitige CAPI-Purchase damit an (gleicher Wert wie Pixel/CAPI external_id).
    try {
      if (id && document.cookie.indexOf('_fx_xid=') === -1) {
        const secure = window.location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = `_fx_xid=${encodeURIComponent(id)}; Max-Age=${180 * 86400}; Path=/; SameSite=Lax${secure}`;
      }
    } catch { /* noop */ }
    return id;
  } catch { return null; }
};

/** Art.-7-Nachweis: Cookie-Entscheidung serverseitig protokollieren (fire-and-forget). */
export function recordCookieConsent({ analytics, marketing, choice }) {
  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/consents/cookies`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      choice,
      analytics: !!analytics,
      marketing: !!marketing,
      consent_version: COOKIE_CONSENT_VERSION,
      visitor_id: visitorId(),
      page: window.location.pathname,
    }),
  }).catch(() => {});
}

/** Persistiert die Auswahl (v3 + Legacy-Key), wendet sie an und protokolliert sie im Backend. */
export function saveConsent({ analytics, marketing, choice }) {
  try {
    localStorage.setItem(CONSENT_KEY_V3, JSON.stringify({
      analytics: !!analytics, marketing: !!marketing, choice,
      ts: new Date().toISOString(), version: COOKIE_CONSENT_VERSION,
    }));
    localStorage.setItem(CONSENT_KEY, analytics && marketing ? 'all' : 'essential');
  } catch { /* noop */ }
  applyConsent({ analytics, marketing });
  recordCookieConsent({ analytics, marketing, choice });
}

/** Legacy-Wrapper (z. B. Reset-Button auf der Cookie-Seite): alles an/aus. */
export function updateGoogleConsent(granted) {
  applyConsent({ analytics: granted, marketing: granted });
}

/** Meta-Pixel Standard-Event (no-op ohne Pixel; optionale Dedup via once-Key). */
export function trackFbq(event, params, { eventId, once } = {}) {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  if (once) {
    const key = `fbconv_${once}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch { /* noop */ }
  }
  if (eventId) window.fbq('track', event, params || {}, { eventID: eventId });
  else window.fbq('track', event, params || {});
}

const META_PIXEL_ID = '3622782127796918'; // muss mit public/index.html übereinstimmen

const getCookie = (name) => {
  const row = document.cookie.split('; ').find((r) => r.startsWith(name + '='));
  return row ? decodeURIComponent(row.slice(name.length + 1)) : undefined;
};

const setCookie = (name, value, days = 90) => {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${days * 86400}; Path=/; SameSite=Lax${secure}`;
  } catch { /* noop */ }
};

/**
 * Meta „Parameter-Builder"-Äquivalent: stellt fbc/fbp robust bereit.
 * - fbc aus dem fbclid der URL bauen (Format fb.1.<ms>.<fbclid>) und 90 Tage persistieren.
 * - Persistierte Werte werden bei JEDEM Event mitgesendet — auch ohne fbclid in der URL.
 */
export function ensureMetaIds() {
  if (typeof window === 'undefined') return { fbc: undefined, fbp: undefined };
  const now = Date.now();
  let fbc = getCookie('_fbc');
  try {
    const fbclid = new URLSearchParams(window.location.search).get('fbclid');
    if (fbclid && (!fbc || !fbc.endsWith(`.${fbclid}`))) {
      fbc = `fb.1.${now}.${fbclid}`; // fbclid unverändert (case-sensitive) übernehmen
      setCookie('_fbc', fbc);
    }
  } catch { /* noop */ }
  let fbp = getCookie('_fbp');
  if (!fbp || !/^fb\.\d+\.\d+\.\d+$/.test(fbp)) {
    fbp = `fb.1.${now}.${Math.floor(1e9 + Math.random() * 9e9)}`;
    setCookie('_fbp', fbp);
  }
  return { fbc, fbp };
}

/** Stabile, anonyme Besucher-ID als external_id-Quelle — roh (identisch in Pixel & CAPI). */
const metaExternalId = () => visitorId();

/** Bekannte E-Mail für besseren Event-Abgleich (z. B. eingeloggter User) — nur mit Marketing-Consent. */
const KNOWN_EMAIL_KEY = 'fuelx_meta_em';
export function setKnownMetaEmail(email) {
  try {
    if (email && hasMarketingConsent()) localStorage.setItem(KNOWN_EMAIL_KEY, String(email).trim().toLowerCase());
    else if (!email) localStorage.removeItem(KNOWN_EMAIL_KEY);
  } catch { /* noop */ }
  setPixelAdvancedMatching();
}
const knownMetaEmail = () => {
  try { return localStorage.getItem(KNOWN_EMAIL_KEY) || undefined; } catch { return undefined; }
};

/** Pixel Advanced Matching (external_id + optional em) — verbessert Match-Qualität konsistent zur CAPI. */
export function setPixelAdvancedMatching() {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  if (!hasMarketingConsent()) return;
  try {
    const am = {};
    const ext = metaExternalId();
    if (ext) am.external_id = ext;      // roh — CAPI sendet denselben Rohwert
    const em = knownMetaEmail();
    if (em) am.em = em;                 // Pixel hasht selbst
    if (Object.keys(am).length) window.fbq('init', META_PIXEL_ID, am);
  } catch { /* noop */ }
}

/** Optionaler Test-Event-Code (Events-Manager „Test Events") — via ?meta_test=CODE aktivierbar,
 * für die restliche Session gemerkt. Betrifft NUR die eigene Session (Produktions-Traffic bleibt unberührt). */
function getMetaTestCode() {
  if (typeof window === 'undefined') return undefined;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('meta_test');
    if (fromUrl) { sessionStorage.setItem('fuelx_meta_test', fromUrl); return fromUrl.slice(0, 40); }
    const saved = sessionStorage.getItem('fuelx_meta_test');
    return saved ? saved.slice(0, 40) : undefined;
  } catch { return undefined; }
}

// Letztes CAPI-PageView (für Beacon-Fallback bei „Tab verlassen") + Dedup-Guard.
let lastCapiPageView = null; // { id, eventTime, url, sent }
let pvBeaconedId = null;

/** Serverseitiges CAPI-Event senden (nur mit Marketing-Consent; Dedup über event_id).
 * keepalive:true → Request überlebt schnelle Navigationen/Unloads (verbessert PageView-Coverage).
 * event_time (Unix-Sekunden) wird gemeinsam mit dem Pixel-Event gesetzt → deckungsgleiche Dedup/Freshness. */
function sendCapiEvent(event, id, { params, email, phone, firstName, lastName, eventTime } = {}) {
  if (!hasMarketingConsent()) return;
  const { fbc, fbp } = ensureMetaIds();
  const et = eventTime || Math.floor(Date.now() / 1000);
  const url = window.location.href;
  if (event === 'PageView') {
    lastCapiPageView = { id, eventTime: et, url, sent: false };
    if (pvBeaconedId !== id) pvBeaconedId = null; // neue PageView darf bei Tab-Leave neu gebeaconed werden
  }
  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/meta/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
    body: JSON.stringify({
      event_name: event,
      event_id: id,
      event_time: et,
      event_source_url: url,
      consent_marketing: true,
      email: email || knownMetaEmail() || undefined,
      phone: phone || undefined,
      first_name: firstName || undefined,
      last_name: lastName || undefined,
      external_id: metaExternalId() || undefined,
      fbp,
      fbc: fbc || undefined,
      test_event_code: getMetaTestCode() || undefined,
      custom_data: params || {},
    }),
  }).then((r) => {
    // Primär-Request kam beim Server an → Beacon-Fallback für diese PageView nicht mehr nötig.
    if (r && r.ok && lastCapiPageView && lastCapiPageView.id === id) lastCapiPageView.sent = true;
  }).catch(() => {});
}

/** Browser-Pixel + Conversions API kombiniert (Deduplizierung über identische event_id + gemeinsame event_time).
 * `once` gilt für das GESAMTE Event (Pixel UND CAPI) → verhindert Doppelzählung serverseitig. */
export function trackMetaEvent(event, { params, eventId, email, phone, firstName, lastName, once } = {}) {
  if (once) {
    const key = `fbconv_${once}`;
    try {
      if (typeof sessionStorage !== 'undefined') {
        if (sessionStorage.getItem(key)) return;
        sessionStorage.setItem(key, '1');
      }
    } catch { /* noop */ }
  }
  const id = eventId || `${event.toLowerCase()}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  const eventTime = Math.floor(Date.now() / 1000); // Pixel + CAPI feuern hier gleichzeitig → identische Zeit
  trackFbq(event, params, { eventId: id });
  sendCapiEvent(event, id, { params, email, phone, firstName, lastName, eventTime });
}

// ─────────────────────────────────────────────────────────────────────────────
// OpenAI Conversions Pixel (oaiq) + serverseitige Conversions API
// Dedup: pixel_id + event_name + event_id identisch auf Browser & Server.
// ─────────────────────────────────────────────────────────────────────────────
export const OPENAI_PIXEL_ID = '6rGHMRmDTE9Yo663tq2PVj';

/** oppref (Klick-Attribution) aus URL/First-Party-Cookie — fürs Server-Relay. */
function oaiOppref() {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('oppref');
    if (fromUrl) return fromUrl.slice(0, 200);
    return getCookie('oppref') || getCookie('_oai_oppref') || undefined;
  } catch { return undefined; }
}

/**
 * OpenAI-Conversion feuern: Browser-Pixel (oaiq measure) + serverseitiges Relay (Conversions API).
 * Beide teilen dieselbe event_id → serverseitige Deduplizierung. Nur mit Marketing-Consent.
 * @param {'page_viewed'|'contents_viewed'|'checkout_started'|'order_created'} eventType
 * @param {{amountCents?:number,currency?:string,eventId?:string,once?:string}} [opts]
 */
export function trackOaiq(eventType, { amountCents, currency = 'EUR', eventId, once } = {}) {
  if (typeof window === 'undefined') return;
  if (!hasMarketingConsent()) return;
  if (once) {
    const key = `oaiconv_${once}`;
    try {
      if (typeof sessionStorage !== 'undefined') {
        if (sessionStorage.getItem(key)) return;
        sessionStorage.setItem(key, '1');
      }
    } catch { /* noop */ }
  }
  const id = eventId || `oai_${eventType}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  const hasValue = typeof amountCents === 'number' && amountCents > 0;
  const data = { type: 'contents' };
  if (hasValue) { data.amount = Math.round(amountCents); data.currency = currency; }
  // Browser-Pixel (SDK ist erst nach Consent geladen; Aufrufe werden bis dahin gequeued)
  try {
    if (typeof window.oaiq === 'function') window.oaiq('measure', eventType, data, { event_id: id });
  } catch { /* noop */ }
  // Server-Relay (Conversions API) — gleiche event_id, keepalive überlebt Navigationen
  try {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/openai/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        event_type: eventType,
        event_id: id,
        source_url: window.location.href,
        consent_marketing: true,
        timestamp_ms: Date.now(),
        amount_cents: hasValue ? Math.round(amountCents) : undefined,
        currency: hasValue ? currency : undefined,
        oppref: oaiOppref(),
      }),
    }).catch(() => {});
  } catch { /* noop */ }
}

/**
 * Beacon-Fallback: Wenn der Nutzer den Tab verlässt (visibilitychange→hidden / pagehide),
 * sichern wir das letzte PageView-CAPI-Event per sendBeacon nach — nur falls der Primär-Request
 * (keepalive-fetch) noch NICHT beim Server angekommen ist. Deckt abbrechende Sessions ab.
 * Gleiche event_id → dedupliziert serverseitig (kein Doppel-Event bei Meta).
 */
function sendPageViewBeacon() {
  if (typeof navigator === 'undefined' || !navigator.sendBeacon) return;
  if (!lastCapiPageView || lastCapiPageView.sent) return;      // Primär bereits durch
  if (pvBeaconedId === lastCapiPageView.id) return;            // schon per Beacon gesichert
  if (!hasMarketingConsent()) return;
  pvBeaconedId = lastCapiPageView.id;
  const { fbc, fbp } = ensureMetaIds();
  const payload = {
    event_name: 'PageView',
    event_id: lastCapiPageView.id,
    event_time: lastCapiPageView.eventTime,
    event_source_url: lastCapiPageView.url,
    consent_marketing: true,
    email: knownMetaEmail() || undefined,
    external_id: metaExternalId() || undefined,
    fbp,
    fbc: fbc || undefined,
    test_event_code: getMetaTestCode() || undefined,
    custom_data: {},
  };
  try {
    // Same-Origin-Beacon mit application/json (kein Preflight) → FastAPI parst es als MetaEventIn.
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    navigator.sendBeacon(`${process.env.REACT_APP_BACKEND_URL}/api/meta/events`, blob);
  } catch { /* noop */ }
}

if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') sendPageViewBeacon();
  });
  window.addEventListener('pagehide', sendPageViewBeacon);
}

/**
 * SPA-Seitenaufruf: Meta PageView (Browser + CAPI dedupliziert) + GA4 page_view.
 * Beim ersten Aufruf wurde der Browser-PageView bereits in index.html gefeuert
 * (event_id in window.__fbqPvId) — dann nur das CAPI-Gegenstück senden.
 */
let firstPageView = true;
let initialPvCapiSent = false;
let lastPv = { path: null, ts: 0 };

/**
 * CAPI-Gegenstück zum initialen Pixel-PageView (aus index.html) — dedupliziert via window.__fbqPvId.
 * Wird ausgelöst, sobald Marketing-Consent vorliegt (auch nachträglich beim Consent-Grant),
 * damit JEDER Session-Start serverseitig abgedeckt ist.
 */
function sendInitialPageViewCapi() {
  if (initialPvCapiSent) return;
  if (typeof window === 'undefined' || !window.__fbqPvId) return;
  if (!hasMarketingConsent()) return;
  initialPvCapiSent = true;
  sendCapiEvent('PageView', window.__fbqPvId, { eventTime: window.__fbqPvTime });
}

export function trackPageView(pathname) {
  if (typeof window === 'undefined') return;
  // Guard gegen Doppel-Aufrufe (React StrictMode / Remounts)
  const now = Date.now();
  if (lastPv.path === pathname && now - lastPv.ts < 1500) return;
  lastPv = { path: pathname, ts: now };
  // OpenAI page_viewed (Pixel + Conversions API). Erster Aufruf via once-Key (deckt sich mit applyConsent bei Spät-Consent).
  trackOaiq('page_viewed', firstPageView ? { once: 'oai_pv_session' } : undefined);
  if (firstPageView) {
    firstPageView = false;
    sendInitialPageViewCapi(); // CAPI zum index.html-PageView (dedup via __fbqPvId)
    return;
  }
  trackMetaEvent('PageView');
  trackGA4Event('page_view', { page_location: window.location.href, page_path: pathname, page_title: document.title });
}

export const CONVERSIONS = {
  PURCHASE: 'AW-18375414040/ZiSCCO3isN0cEJiiirpE',
  NEWSLETTER: 'AW-18375414040/GZ1cCJfysN0cEJiiirpE',
  LEAD_FORM: 'AW-18375414040/ppDMCN2Xsd0cEJiiirpE',
  CONTACT: 'AW-18375414040/OM2SCJrOoN0cEJiiirpE',
  ADD_TO_CART: 'AW-18375414040/rA3RCIGwsd0cEJiiirpE',
  START_PAYMENT: 'AW-18375414040/OPu4CLWbtN0cEJiiirpE',
  // Account-Registrierung — nutzt standardmäßig das NEWSLETTER-Label,
  // kann in Google Ads als eigenes Sign-Up-Conversion angelegt und via ENV
  // überschrieben werden: REACT_APP_GADS_SIGNUP_LABEL="AW-XXX/YYY".
  SIGN_UP: process.env.REACT_APP_GADS_SIGNUP_LABEL || 'AW-18375414040/GZ1cCJfysN0cEJiiirpE',
  // PWA Install — Label wird via ENV gesetzt, damit du in Google Ads
  // ein neues Conversion-Action anlegen und das Label hier hinterlegen kannst
  // ohne Code-Änderung. Fallback: nichts wird gesendet, wenn Label nicht gesetzt.
  PWA_INSTALL: process.env.REACT_APP_GADS_PWA_INSTALL_LABEL || '',
};

/**
 * Bereinigt und normalisiert Enhanced-Conversion-Kundendaten für Google Ads.
 * gtag.js hasht E-Mail/Telefon clientseitig automatisch (SHA-256) — wir müssen
 * NUR normalisieren (lowercase, trim, E.164 für Telefon). Adressfelder werden
 * ebenfalls automatisch gehasht, sofern verwendet.
 */
function buildEnhancedUserData(userData) {
  if (!userData || typeof userData !== 'object') return null;
  const out = {};
  if (userData.email) out.email = String(userData.email).trim().toLowerCase();
  if (userData.phone) {
    // In E.164 umwandeln: +49… Falls keine +, deutsche Vorwahl annehmen.
    let p = String(userData.phone).replace(/[^\d+]/g, '');
    if (p && !p.startsWith('+')) {
      if (p.startsWith('00')) p = '+' + p.slice(2);
      else if (p.startsWith('0')) p = '+49' + p.slice(1);
      else p = '+' + p;
    }
    out.phone_number = p;
  }
  const address = {};
  if (userData.firstName) address.first_name = String(userData.firstName).trim().toLowerCase();
  if (userData.lastName) address.last_name = String(userData.lastName).trim().toLowerCase();
  if (userData.street) address.street = String(userData.street).trim().toLowerCase();
  if (userData.city) address.city = String(userData.city).trim().toLowerCase();
  if (userData.region) address.region = String(userData.region).trim().toLowerCase();
  if (userData.postalCode) address.postal_code = String(userData.postalCode).trim();
  if (userData.country) address.country = String(userData.country).trim().toUpperCase();
  if (Object.keys(address).length) out.address = address;
  return Object.keys(out).length ? out : null;
}

/**
 * Feuert eine Google-Ads-Conversion.
 * @param {string} sendTo - Conversion-Label (AW-XXX/YYY)
 * @param {object} opts
 * @param {number} opts.value - Wert (EUR). Für Enhanced Conversions & Wertoptimierung.
 * @param {string} [opts.currency='EUR']
 * @param {string} [opts.transactionId] - Für Dedup (Purchase MUSS eine haben)
 * @param {string} [opts.once] - Session-Dedup-Key
 * @param {object} [opts.userData] - { email, phone, firstName, lastName, street, city, region, postalCode, country }
 *                                   → wird für Enhanced Conversions als user_data mitgesendet
 */
export function trackConversion(sendTo, { value = 1.0, currency = 'EUR', transactionId, once, userData } = {}) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  if (!sendTo) return; // Guard: kein Fire, wenn Conversion-Label leer (z.B. PWA_INSTALL ohne ENV)
  if (once) {
    const key = `gconv_${once}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch { /* noop */ }
  }
  // Enhanced Conversions: user_data VOR dem Event setzen, gtag hasht automatisch.
  // Nur bei gültigem Marketing-Consent (ansonsten würde Google es sowieso verwerfen).
  // Wichtig: Nach dem Event wieder auf {} zurücksetzen, damit spätere Conversions
  // ohne userData nicht die PII des vorherigen Kunden erben (globaler gtag-State).
  const enhanced = hasMarketingConsent() ? buildEnhancedUserData(userData) : null;
  if (enhanced) {
    try { window.gtag('set', 'user_data', enhanced); } catch { /* noop */ }
  }
  const params = { send_to: sendTo, value, currency };
  if (transactionId) params.transaction_id = transactionId;
  window.gtag('event', 'conversion', params);
  if (enhanced) {
    // user_data wieder leeren — verhindert PII-Vererbung an spätere Events derselben Session
    try { window.gtag('set', 'user_data', {}); } catch { /* noop */ }
  }
}

/**
 * Speichert Kundendaten aus dem Checkout-Formular in sessionStorage, damit
 * die Success-Page (nach Stripe-Redirect) Enhanced-Conversions-Daten hat OHNE
 * dass der Backend-Status-Endpoint PII zurückgeben muss (GDPR — Iteration 249).
 * TTL: 1 h; wird nach Verwendung von der Success-Page gelöscht.
 */
export function stashCheckoutCustomer(orderId, customer) {
  if (!orderId || !customer || typeof window === 'undefined') return;
  try {
    const payload = {
      email: customer.email,
      phone: customer.phone,
      firstName: customer.first_name || customer.firstName,
      lastName: customer.last_name || customer.lastName,
      street: customer.address_line1 || customer.street,
      postalCode: customer.address_zip || customer.postalCode,
      city: customer.address_city || customer.city,
      country: customer.address_country || customer.country || 'DE',
      _ts: Date.now(),
    };
    sessionStorage.setItem(`fuelx_checkout_pii_${orderId}`, JSON.stringify(payload));
  } catch { /* noop */ }
}

/**
 * Liest die zu Beginn des Checkouts gestashten Kundendaten aus sessionStorage.
 * Nach dem Lesen wird der Eintrag gelöscht (One-Shot). TTL 1 h.
 */
export function readAndClearCheckoutCustomer(orderId) {
  if (!orderId || typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(`fuelx_checkout_pii_${orderId}`);
    if (!raw) return null;
    const p = JSON.parse(raw);
    sessionStorage.removeItem(`fuelx_checkout_pii_${orderId}`);
    if (!p._ts || Date.now() - p._ts > 60 * 60 * 1000) return null;
    return p;
  } catch { return null; }
}

/**
 * GA4-Event senden (generisch). Nur wenn analytics-Consent gegeben ist.
 * Beispiel: trackGA4Event('pwa_installed', { source: 'button', platform: 'android' })
 */
export function trackGA4Event(name, params = {}) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  const consent = getConsent();
  if (!consent?.analytics) return;
  window.gtag('event', name, params);
}

/**
 * GA4-Conversion/Schlüsselereignis (z. B. 'purchase', 'sign_up').
 * Sendet über gtag UNABHÄNGIG vom Analytics-Consent — Consent Mode v2 regelt die Cookies
 * (cookieless/modelliert bei Ablehnung). So werden Conversions in GA4 vollständig gemessen.
 * 'purchase' ist in GA4 automatisch ein Schlüsselereignis; 'sign_up' muss in GA4 einmalig
 * als Schlüsselereignis markiert werden (Verwaltung → Ereignisse).
 */
export function trackGA4Conversion(name, params = {}) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', name, params);
}


